import { create } from 'zustand'
import type { AnswerTierId } from '@/features/companion/answer-tiers'

/**
 * App-wide answer-tier preference — persisted across sessions.
 *
 * The stored tier may be higher than what the user's plan allows (e.g. they
 * downgraded). Callers must clamp it via `clampTierToPlan(tier, planTier)`
 * before sending to the backend. The store holds the *preference*, not the
 * *effective* tier — this avoids silently overwriting the preference on plan
 * change.
 *
 * Default: 'normal' (the balanced tier; gets clamped to 'fast' for basic plans
 * at the call site).
 */

const TIER_KEY = 'edge:clicky:tier'
const DEFAULT_TIER: AnswerTierId = 'normal'

function isValidTier(value: string): value is AnswerTierId {
  return value === 'fast' || value === 'normal' || value === 'heavy'
}

function readPersisted(): AnswerTierId {
  try {
    const stored = localStorage.getItem(TIER_KEY)
    if (stored && isValidTier(stored)) return stored
  } catch {
    /* ignore quota / unavailable storage */
  }
  return DEFAULT_TIER
}

function persist(tier: AnswerTierId): void {
  try {
    localStorage.setItem(TIER_KEY, tier)
  } catch {
    /* ignore */
  }
}

interface TierStoreState {
  tier: AnswerTierId
  setTier: (tier: AnswerTierId) => void
}

export const useTierStore = create<TierStoreState>((set) => ({
  tier: readPersisted(),
  setTier: (tier) => {
    persist(tier)
    set({ tier })
  },
}))
